import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Button } from "react-bootstrap";

import { FiX } from "react-icons/fi";

import { ReactComponent as CameraIcon } from "../../assets/images/camera-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { getViewProfile, updateProfile } from "../../redux/slices/profileSlice";

const EditProfileModal = ({
  show,
  onHide,
  profileData = {},
  onSave = () => {},
  isApplyPage = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    ...profileData,
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const store = useSelector((state) => state.profile);
  const dispatch = useDispatch();

  // useEffect(() => {
  //   if (show) {
  //     setFormData({
  //       name: store?.profileItem?.data?.full_name || "",
  //       email: store?.profileItem?.data?.email || "",
  //       mobile: store?.profileItem?.data?.phone || "",
  //     });
  //     // Reset image states when modal opens
  //     setProfileImage(null);
  //     setImagePreview(null);
  //   }
  // }, [show, profileData, store?.profileItem?.data]);
  useEffect(() => {
    if (!show) return;

    if (isApplyPage) {
      // ✅ APPLY PAGE → use passed data
      setFormData({
        name: profileData?.name || "",
        email: profileData?.email || "",
        mobile: profileData?.mobile || "",
      });
    } else {
      // ✅ PROFILE PAGE → use API store
      setFormData({
        name: store?.profileItem?.data?.full_name || "",
        email: store?.profileItem?.data?.email || "",
        mobile: store?.profileItem?.data?.phone || "",
      });
    }

    // reset image
    setProfileImage(null);
    setImagePreview(null);
  }, [show, isApplyPage, profileData, store?.profileItem?.data]);

  // Cleanup preview URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);

      // Create preview URL for the new image
      const previewUrl = URL.createObjectURL(file);

      // Revoke old preview URL to prevent memory leaks
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }

      setImagePreview(previewUrl);
    }
  };

  const handleImageClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // const onSubmit = () => {
  //   const formDataToSend = new FormData();

  //   // Append text fields
  //   if (formData.name) formDataToSend.append("name", formData.name);
  //   if (formData.mobile) formDataToSend.append("phone", formData.mobile);
  //   if (formData.email) formDataToSend.append("email", formData.email);
  //   if (profileImage && profileImage instanceof File) {
  //     formDataToSend.append("profile_photo", profileImage);
  //   }

  //   // Dispatch with FormData
  //   dispatch(updateProfile(formDataToSend));
  // };
  const onSubmit = async () => {
    if (isApplyPage) {
      // 🔥 APPLY PAGE LOGIC
      const hadNoPhoneInitially = !profileData?.mobile || profileData?.mobile === "";
      const hasPhoneNow = formData.mobile && formData.mobile.trim() !== "";

      // If user had no phone initially and now added one → API call for phone only
      if (hadNoPhoneInitially && hasPhoneNow) {
        const formDataToSend = new FormData();
        
        // API requires name field, so include it
        if (formData.name) formDataToSend.append("name", formData.name);
        formDataToSend.append("phone", formData.mobile);

        try {
          await dispatch(updateProfile(formDataToSend)).unwrap();
          
          // After API success, update locally with all data
          onSave({
            name: formData.name,
            mobile: formData.mobile,
            email: formData.email,
            profileImage,
          });
          
          onHide();
        } catch (error) {
          console.error("Failed to update phone:", error);
          // Still update locally even if API fails
          onSave({
            name: formData.name,
            mobile: formData.mobile,
            email: formData.email,
            profileImage,
          });
          onHide();
        }
        return;
      }

      // Otherwise → LOCAL UPDATE ONLY (phone already existed or still empty)
      onSave({
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        profileImage,
      });

      onHide();
      return;
    }

    // ✅ PROFILE PAGE → API CALL FOR ALL FIELDS
    const formDataToSend = new FormData();

    if (formData.name) formDataToSend.append("name", formData.name);
    if (formData.mobile) formDataToSend.append("phone", formData.mobile);
    if (formData.email) formDataToSend.append("email", formData.email);

    if (profileImage && profileImage instanceof File) {
      formDataToSend.append("profile_photo", profileImage);
    }

    dispatch(updateProfile(formDataToSend));
  };
  useEffect(() => {
    if (store?.actionFlag === "PROFILE_UPDT_SCS") {
      if (!isApplyPage) {
        // Only auto-close and refresh for profile page
        onHide();
        dispatch(getViewProfile());
      }
      // For apply page, the onSubmit already handles closing after API success
    }
  }, [dispatch, store?.actionFlag, isApplyPage, onHide]);

  const emailSkipped =
    sessionStorage.getItem("emailCaptureModalDismissed") === "true";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="custom-modal profile-modal"
    >
      {/* <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center">Edit Profile</Modal.Title>
      </Modal.Header> */}

      <Modal.Header>
        <h5 className="modal-title">Edit Profile</h5>
        <Button className="close-btn" onClick={onHide}>
          <FiX size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <div className="profile-image" onClick={handleImageClick}>
          <img
            src={
              imagePreview ||
              (store?.profileItem?.data?.profile_photo_path
                ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${store?.profileItem?.data?.profile_photo_path}`
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    store?.profileItem?.data?.full_name || "User",
                  )}&background=0195D3&color=fff&size=200`)
            }
            alt="Profile"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                store?.profileItem?.data?.full_name || "User",
              )}&background=0195D3&color=fff&size=200`;
            }}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
          <button className="btn-profile-icon">
            <CameraIcon />
          </button>
        </div>

        <Form>
          <Form.Group className="form-group">
            <Form.Label className="col-label">Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleChange}
              className="col-input"
            />
          </Form.Group>

          <Form.Group className="form-group">
            <Form.Label className="col-label">Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              className="col-input"
              // disabled
              disabled={!isApplyPage && !emailSkipped}
            />
          </Form.Group>

          <Form.Group className="form-group">
            <Form.Label className="col-label">Phone number</Form.Label>
            <Form.Control
              type="text"
              name="mobile"
              value={formData.mobile || ""}
              onChange={handleChange}
              className="col-input"
              placeholder="Example: +1234567890"
            />
          </Form.Group>

          <Form.Group className="form-group mb-0 mt-4">
            <Button onClick={onSubmit} className="btn btn-primary w-100">
              Save Changes
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditProfileModal;
